import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { soldeActions } from "../../sagas/soldeSlice";
import { Container, Row, Col, Card, CardBody, Button, Form, FormGroup, Label, Input, Table, Spinner } from "reactstrap";
import { FaTruck, FaFileExport, FaSearch, FaFilePdf, FaTrash } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { format } from "date-fns";
import { supplierActions } from "../../sagas/supplierSlice";
import { formatPrice } from "../../helpers/utility";
import pdfMake from "pdfmake/build/pdfmake";
import Swal from "sweetalert2";

const formatMoney = (n) =>
    (Number(n || 0))
        .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const getSupplierBalanceMeta = (value) => {
    const amount = Number(value || 0);
    if (amount > 0) {
        return { colorClass: "text-success", colorHex: "#198754", prefix: "+", label: "Solde" };
    }
    if (amount < 0) {
        return { colorClass: "text-danger", colorHex: "#dc3545", prefix: "-", label: "Solde" };
    }
    return { colorClass: "text-dark", colorHex: "#0F172A", prefix: "", label: "Solde fournisseur nul" };
};

const renderSupplierBalance = (value) => {
    const meta = getSupplierBalanceMeta(value);
    const amount = Number(value || 0);

    return (
        <b>
            <span className={meta.colorClass}>
                {meta.prefix}
                {formatMoney(Math.abs(amount))}
            </span>
        </b>
    );
};

const MouvementSoldeFournisseur = () => {
    const { soldesFournisseur, loading, error } = useSelector((state) => state.soldes);
    const fournisseurs = useSelector(state => state.suppliers.suppliers);
    const { t } = useTranslation();
    const { company, role } = useSelector(state => state.auth);
    const isSuperAdmin = role === "ROLE_SUPER_ADMIN";

    const startDate = format(new Date(new Date().getFullYear(), 0, 1), 'yyyy-MM-dd');
    const endDate = format(new Date(Date.now() + 24 * 60 * 60 * 1000), 'yyyy-MM-dd');
    const pad = (n) => n.toString().padStart(2, '0');
    const [filters, setFilters] = useState({
        fournisseurId: null,
        dateMouvementFrom: startDate,
        dateMouvementTo: endDate,
        typeDocument: ''
    });

    const dispatch = useDispatch();
    console.log(soldesFournisseur)

    useEffect(() => {
        dispatch(supplierActions.search({
            query: ""
        }));
        dispatch(soldeActions.searchFournisseur(filters));
    }, []);

    const handleSearch = (e) => {
        e && e.preventDefault();
        dispatch(soldeActions.searchFournisseur(filters));
    };

    const handleDelete = async (mouvementId) => {
        const result = await Swal.fire({
            icon: "warning",
            title: "Supprimer ce mouvement ?",
            text: "Les soldes suivants seront recalculés.",
            showCancelButton: true,
            confirmButtonColor: "#dc3545",
            confirmButtonText: "Supprimer",
            cancelButtonText: "Annuler",
        });
        if (!result.isConfirmed) return;
        dispatch(soldeActions.deleteSupplierMovement({
            id: mouvementId,
            onSuccess: () => {
                Swal.fire("Supprimé", "Le mouvement a été supprimé.", "success");
                dispatch(soldeActions.searchFournisseur(filters));
            },
            onError: () => Swal.fire("Erreur", "Suppression impossible.", "error"),
        }));
    };

    const handleExport = () => {
        // Logic to handle export of mouvements
        // This could be a call to a service that generates a report or downloads data
    }

    const founisseurOptions = fournisseurs?.map(fournisseur => ({
        value: fournisseur.id,
        label: fournisseur.typeFournisseur === 'PARTICULIER'
            ? `${fournisseur.nom} ${fournisseur.prenom || ''} `
            : fournisseur.entreprise,
    })) ?? [];


    const founisseurOptionsWithAll = [
        { value: null, label: t('common.all', 'Tous') },
        ...founisseurOptions
    ];

    const totalDebit = soldesFournisseur?.reduce((sum, m) => sum + (Number(m.debit) || 0), 0) || 0;
    const totalCredit = soldesFournisseur?.reduce((sum, m) => sum + (Number(m.credit) || 0), 0) || 0;
    const soldeActuel = soldesFournisseur && soldesFournisseur.length > 0 ? soldesFournisseur[soldesFournisseur.length - 1].soldeApres : 0;
    const soldeMeta = getSupplierBalanceMeta(soldeActuel);

    const selectedFournisseurLabel = useMemo(() => {
        if (!filters.fournisseurId) return t("common.all", "Tous");
        const f = founisseurOptions.find((c) => String(c.value) === String(filters.fournisseurId));
        return f?.label || t("common.unknown", "Inconnu");
    }, [filters.fournisseurId, founisseurOptions, t]);

    const handleGeneratePdf = () => {
        // expects: t, filters, soldes, selectedClientLabel, company, pdfMake available in scope
        const title = t("nav.supplierReports", "Journal des opérations");
        const periodLabel = `${t("common.from", "Du")} ${filters.dateMouvementFrom} ${t("common.to", "au")} ${filters.dateMouvementTo}`;
        const clientLabel = `${t("reglement.fournisseur")} : ${selectedFournisseurLabel}`;

        // Helpers
        const fmt = (n) =>
            formatPrice(n);
        const signed = (n) => {
            const x = Number(n || 0);
            if (x > 0) return `+${fmt(x)}`;
            if (x < 0) return `-${fmt(Math.abs(x))}`;
            return "0.00";
        };
        const supplierSigned = (n) => {
            const meta = getSupplierBalanceMeta(n);
            return `${meta.prefix}${fmt(Math.abs(Number(n || 0)))}`;
        };
        const joinNonEmpty = (arr, sep) => arr.filter(Boolean).join(sep);

        // Calculations
        const totalDebit = soldesFournisseur?.reduce((s, m) => s + (Number(m.debit) || 0), 0) || 0;
        const totalCredit = soldesFournisseur?.reduce((s, m) => s + (Number(m.credit) || 0), 0) || 0;
        const soldeActuel = soldesFournisseur && soldesFournisseur.length ? Number(soldesFournisseur[soldesFournisseur.length - 1].soldeApres || 0) : 0;

        // Header (only name + subtitle)
        const headerStack = [
            { text: company?.name || "", alignment: "center", bold: true, fontSize: 14, color: "#0F172A", margin: [0, 2, 0, 0] },
            company?.subtitle ? { text: company.subtitle, alignment: "center", fontSize: 11, color: "#475569", margin: [0, 2, 0, 6] } : null,
            { canvas: [{ type: "line", x1: 0, y1: 6, x2: 760, y2: 6, lineWidth: 0.5, color: "#CBD5E1" }] },
            { text: title, alignment: "center", bold: true, fontSize: 12, margin: [0, 10, 0, 0], color: "#0F172A" },
            { text: `${clientLabel}  |  ${periodLabel}`, alignment: "center", fontSize: 9, color: "#475569", margin: [0, 4, 0, 0] },
        ].filter(Boolean);

        // Footer (company info centered)
        const footerAddress = joinNonEmpty([company?.address], " ");
        const footerContacts = joinNonEmpty([company?.phone1, company?.phone2, company?.email], "  |  ");
        const footerLegal = joinNonEmpty([
            company?.identificationFiscale ? `IF: ${company.identificationFiscale}` : null,
            company?.ice ? `ICE: ${company.ice}` : null,
            company?.rc ? `RC: ${company.rc}` : null,
            company?.patente ? `Patente: ${company.patente}` : null,
            company?.cnss ? `CNSS: ${company.cnss}` : null,
        ], "   â€¢   ");
        const footerBank = joinNonEmpty([
            company?.bankName ? `${company.bankName}` : null,
            company?.RIB ? `RIB: ${company.RIB}` : null,
        ], "   â€¢   ");

        // Table rows
        const headerRow = [
            { text: t("common.reference", "Référence"), style: "thead" },
            { text: t("reglement.fournisseur", "Fournisseur"), style: "thead" },
            { text: t("common.operation", "Opération"), style: "thead" },
            { text: t("common.da", "Date Opération"), style: "thead" },
            { text: t("common.debit", "Débit"), style: "thead", alignment: "right" },
            { text: t("common.credit", "Crédit"), style: "thead", alignment: "right" },
            { text: t("common.balanceBefore", "Solde Avant"), style: "thead", alignment: "right" },
            { text: t("common.balanceAfter", "Solde Après"), style: "thead", alignment: "right" },
        ];

        const rows = (soldesFournisseur && soldesFournisseur.length)
            ? soldesFournisseur.map((m) => ([
                { text: m.reference || "-", noWrap: true },
                { text: m.fournisseur || "-" },
                { text: m.description || "-" },
                { text: m.dateOperation || "-", noWrap: true },
                { text: fmt(m.debit), alignment: "right" },
                { text: fmt(m.credit), alignment: "right" },
                {
                    text: supplierSigned(m.soldeAvant),
                    alignment: "right",
                    color: getSupplierBalanceMeta(m.soldeAvant).colorHex,
                },
                {
                    text: supplierSigned(m.soldeApres),
                    alignment: "right",
                    color: getSupplierBalanceMeta(m.soldeApres).colorHex,
                },
            ]))
            : [[{ text: t("mouvements.noData", "Aucun mouvement trouvé."), colSpan: 8, alignment: "center", italics: true, color: "#6c757d" }, {}, {}, {}, {}, {}, {}, {}]];

        const totalsRow = [
            { text: t("common.totals", "Totaux :"), colSpan: 4, alignment: "right", bold: true }, {}, {}, {},
            { text: fmt(totalDebit), alignment: "right", bold: true },
            { text: fmt(totalCredit), alignment: "right", bold: true },
            { text: `${t("common.balanceAt", "Solde au")} ${filters.dateMouvementTo} :`, alignment: "right", bold: true },
            {
                text: supplierSigned(soldeActuel),
                alignment: "right",
                bold: true,
                color: soldeMeta.colorHex,
            },
        ];

        // Summary block (below the table): Débit, Crédit, Solde
        const summaryBlock = {
            margin: [0, 10, 0, 0],
            table: {
                widths: ["*", "*", "*"],
                body: [
                    [
                        { text: "Débit", style: "sumLabel", alignment: "center" },
                        { text: "Crédit", style: "sumLabel", alignment: "center" },
                        { text: soldeMeta.label, style: "sumLabel", alignment: "center" },
                    ],
                    [
                        { text: fmt(totalDebit), style: "sumValue", alignment: "center" },
                        { text: fmt(totalCredit), style: "sumValue", alignment: "center" },
                        {
                            text: supplierSigned(soldeActuel),
                            style: "sumValue",
                            alignment: "center",
                            color: soldeMeta.colorHex,
                        },
                    ],
                ],
            },
            layout: {
                fillColor: (rowIndex) => (rowIndex === 0 ? "#E0F2FE" : "#F0F9FF"), // soft blue highlight
                hLineColor: () => "#38BDF8",
                vLineColor: () => "#38BDF8",
                hLineWidth: (i, node) => (i === 0 || i === node.table.body.length ? 0.8 : 0.5),
                vLineWidth: () => 0.5,
                paddingLeft: () => 8,
                paddingRight: () => 8,
                paddingTop: () => 8,
                paddingBottom: () => 8,
            },
        };

        // Document
        const docDefinition = {
            pageOrientation: "landscape",
            pageSize: "A4",
            pageMargins: [40, 100, 40, 90],
            header: { margin: [40, 20, 40, 0], stack: headerStack },
            content: [
                {
                    table: {
                        headerRows: 1,
                        widths: [80, 100, "*", 90, 70, 70, 80, 80],
                        body: [headerRow, ...rows, totalsRow],
                    },
                    layout: {
                        fillColor: (i) => (i === 0 ? "#E9F2FF" : i % 2 === 0 ? "#FFFFFF" : "#F8FAFC"),
                        hLineColor: () => "#E2E8F0",
                        vLineColor: () => "#E2E8F0",
                    },
                },
                summaryBlock, // âœ… placed below the table
            ],
            styles: {
                thead: { bold: true, fontSize: 10, color: "#0F172A" },
                sumLabel: { fontSize: 10, bold: true, color: "#0F172A" },
                sumValue: { fontSize: 12, bold: true, color: "#0F172A" },
            },
            defaultStyle: { fontSize: 9, color: "#0F172A" },
            footer: (currentPage, pageCount) => ({
                margin: [40, 10, 40, 16],
                stack: [
                    footerAddress ? { text: footerAddress, alignment: "center", fontSize: 9, color: "#475569" } : null,
                    footerContacts ? { text: footerContacts, alignment: "center", fontSize: 9, color: "#475569", margin: [0, 2, 0, 0] } : null,
                    footerLegal ? { text: footerLegal, alignment: "center", fontSize: 9, color: "#64748B", margin: [0, 2, 0, 0] } : null,
                    footerBank ? { text: footerBank, alignment: "center", fontSize: 9, color: "#64748B", margin: [0, 2, 0, 6] } : null,
                    {
                        text: `${t("common.generatedOn", "Généré le")} ${format(new Date(), "yyyy-MM-dd HH:mm")}  â€¢  ${currentPage}/${pageCount}`,
                        alignment: "center",
                        fontSize: 8,
                        color: "#94A3B8",
                    },
                ].filter(Boolean),
            }),
        };

        pdfMake.createPdf(docDefinition).open(); // or .download('journal-operations.pdf')
    };

    return (
        <Container fluid>
            <Row className="mb-1">
                <Col>
                    <Card className="shadow-sm">
                        <CardBody className="py-2 d-flex justify-content-between align-items-center">
                            <h4 className="mb-0 d-flex align-items-center">
                                <FaTruck className="text-primary me-2" />
                                {t('nav.supplierReports', 'Opérations fournisseurs')}
                            </h4>
                            <div className="d-flex align-items-center">
                                <Button color="danger" onClick={handleGeneratePdf} className="me-2">
                                    <FaFilePdf className="me-2" />
                                    {t("common.download", "PDF")}
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            {/* Search Filters */}
            <Row className="mb-1">
                <Col>
                    <Card className="shadow-sm">
                        <CardBody className="py-2">
                            <Form inline onSubmit={handleSearch} className="row g-2 align-items-end">
                                <Col md={3}>
                                    <Label htmlFor="fournisseurId" className="mb-1">{t('reception.supplier')}:</Label>
                                    <Select
                                        id="fournisseurId"
                                        options={founisseurOptionsWithAll}
                                        value={founisseurOptionsWithAll.find(option => option.value === filters.fournisseurId)}
                                        onChange={(selectedOption) => setFilters(f => ({ ...f, fournisseurId: selectedOption ? selectedOption.value : null }))}
                                        placeholder={t('reception.selectFournisseur')}
                                        isSearchable
                                        menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                        styles={{
                                            menuPortal: base => ({ ...base, zIndex: 9999 })
                                        }}
                                    />
                                </Col>
                                <Col md={3}>
                                    <FormGroup className="mb-0">
                                        <Label for="dateMouvementFrom" className="mb-1">Date début</Label>
                                        <Input
                                            type="date"
                                            id="dateMouvementFrom"
                                            value={filters.dateMouvementFrom}
                                            onChange={e => setFilters(f => ({ ...f, dateMouvementFrom: e.target.value }))}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={3}>
                                    <FormGroup className="mb-0">
                                        <Label for="dateMouvementTo" className="mb-1">Date fin</Label>
                                        <Input
                                            type="date"
                                            id="dateMouvementTo"
                                            value={filters.dateMouvementTo}
                                            onChange={e => setFilters(f => ({ ...f, dateMouvementTo: e.target.value }))}
                                        />
                                    </FormGroup>
                                </Col>
                                {/* <Col md={3}>
                                    <FormGroup>
                                        <Label for="typeDocument">Type Document</Label>
                                        <Input
                                            type="select"
                                            id="typeDocument"
                                            value={filters.typeDocument}
                                            onChange={e => setFilters(f => ({ ...f, typeDocument: e.target.value }))}
                                        >
                                            <option value="">Tous</option>
                                            <option value="FACTURE">Facture</option>
                                            <option value="BON_LIVRAISON">Bon de livraison</option>
                                            <option value="DEVIS">Devis</option>
                                            <option value="AVOIR">Avoir</option>
                                            <option value="PAIEMENT">Paiement</option>
                                            <option value="AVANCE">Avance</option>
                                            <option value="AJUSTEMENT_MANUEL">Ajustement manuel</option>
                                        </Input>
                                    </FormGroup>
                                </Col> */}
                                <Col md={2} className="d-grid">
                                    <Button color="primary" type="submit">
                                        <FaSearch className="me-2" />
                                        {t('search', 'Rechercher')}
                                    </Button>
                                </Col>
                            </Form>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            {!loading && soldesFournisseur && soldesFournisseur.length > 0 && (
                <Row className="mb-1">
                    <Col md={4} sm={6} className="mb-1">
                        <Card className="shadow-sm border-0">
                            <CardBody className="py-2 px-2">
                                <div className="text-muted small">Débit</div>
                                <div className="fw-bold fs-6 text-danger">{formatMoney(totalDebit)} DH</div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={4} sm={6} className="mb-1">
                        <Card className="shadow-sm border-0">
                            <CardBody className="py-2 px-2">
                                <div className="text-muted small">Crédit</div>
                                <div className="fw-bold fs-6 text-success">{formatMoney(totalCredit)} DH</div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={4} sm={12} className="mb-1">
                        <Card className="shadow-sm border-0">
                            <CardBody className="py-2 px-2">
                                <div className="text-muted small">{soldeMeta.label}</div>
                                <div className={`fw-bold fs-6 ${soldeMeta.colorClass}`}>
                                    {soldeMeta.prefix}
                                    {formatMoney(Math.abs(Number(soldeActuel)))} DH
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            )}
            {/* Table of mouvements */}
            <Row>
                <Col>
                    <Card className="shadow-sm">
                        <CardBody className="p-2">
                            {loading ? (
                                <div className="text-center my-4"><Spinner color="primary" /></div>
                            ) : error ? (
                                <div className="text-danger">Erreur lors du chargement des mouvements.</div>
                            ) : (
                                <Table bordered hover size="sm" className="mb-0 align-middle">
                                    <thead className="table-primary">
                                        <tr>
                                            <th >Référence</th>
                                            <th >Fournisseur</th>
                                            <th >Opération</th>
                                            <th >Date Opération</th>
                                            <th >Débit</th>
                                            <th >Crédit</th>
                                            <th >Solde Avant</th>
                                            <th >Solde Après</th>
                                            {isSuperAdmin && <th className="text-center">Actions</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {soldesFournisseur ? (
                                            soldesFournisseur.map((m, idx) => (
                                                <tr key={idx} className={idx === 0 ? 'fw-bold' : ''}>
                                                    <td>{m.reference}</td>
                                                    <td>{m.fournisseur}</td>
                                                    <td>{m.description}</td>
                                                    <td>{m.dateOperation}</td>
                                                    <td>{formatMoney(m.credit)}</td>
                                                    <td>{formatMoney(m.debit)}</td>
                                                    <td>
                                                        {renderSupplierBalance(m.soldeAvant)}
                                                    </td>
                                                    <td>
                                                        {renderSupplierBalance(m.soldeApres)}
                                                    </td>
                                                    {isSuperAdmin && (
                                                        <td className="text-center">
                                                            {m.mouvementId && (
                                                                <Button color="danger" size="sm" onClick={() => handleDelete(m.mouvementId)}>
                                                                    <FaTrash />
                                                                </Button>
                                                            )}
                                                        </td>
                                                    )}
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={9} className="text-center py-5">
                                                    <div className="d-flex flex-column align-items-center justify-content-center">
                                                        <span style={{ fontSize: '2rem', color: '#adb5bd' }}>
                                                            <FaTruck />
                                                        </span>
                                                        <div className="mt-2 text-muted">
                                                            {t('mouvements.noData', 'Aucun mouvement trouvé.')}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                    {soldesFournisseur && soldesFournisseur.length > 1 ? (<tfoot className="table-light">
                                        <tr className="table-secondary fw-bold">
                                            <td colSpan={4} className="text-end">Totaux :</td>
                                            <td>{totalDebit.toLocaleString()}</td>
                                            <td>{totalCredit.toLocaleString()}</td>
                                            <td colSpan={2} className="text-start">
                                                {`Solde fournisseur au ${filters.dateMouvementTo} : `}
                                                {renderSupplierBalance(soldeActuel)}
                                            </td>
                                        </tr>
                                    </tfoot>) : null}
                                </Table>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Container>
    );

}

export default MouvementSoldeFournisseur;

